import map from 'lodash/map';
import React from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';

import { setDialogIsOpen } from 'Apollo/Store/Showroom/UserView/Share/resolvers';
import { setSelectedPdfs, setShowroomSelectedMedia } from 'Apollo/Store/Showroom/UserView/resolvers';
import {
	useGetShowroomSelectedMediaQuery,
	useGetShowroomShareOptionsQuery,
	QualityOptions,
	useGenerateShowroomArchiveMutation,
	GenerateShowroomArchiveMutationVariables,
	useGetArchiveStatusLazyQuery,
	ArchiveStatus,
	useGetZipFileLazyQuery,
	DownloadFile,
	GetShowroomSelectedMediaQuery
} from 'generated/graphql';

import { useCustomNotifications } from 'lib/Hooks';
import { useDownloadFiles, DownloadFileHookInput } from 'lib/downloadFiles';
import { useIdentifyUser } from 'lib/identifyUser';
import { isImage } from 'lib/media';
import { useGetCurrentShowroomData } from 'lib/showroom';
import { useUserConsent } from 'lib/userConsent';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import MailIcon from '@material-ui/icons/MailOutline';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

import DialogConfirm from 'components/Common/Dialog/Confirm';

import { useStyles } from './styles';

type SelectedShowroomMedia = GetShowroomSelectedMediaQuery['showroom']['userView']['selectedMedias'][0];
type SelectedShowroomPdf = GetShowroomSelectedMediaQuery['showroom']['userView']['selectedPdfs'][0];

const ShowroomContentShare = () => {
	const { track } = React.useContext(ShowroomContext);
	const showroomObj = useGetCurrentShowroomData();
	const { tenant } = showroomObj.tenant;

	const classes = useStyles();

	const {
		data: {
			showroom: {
				userView: {
					share: { isDialogOpen, mediaQuality }
				}
			}
		}
	} = useGetShowroomShareOptionsQuery();
	const {
		data: {
			showroom: {
				userView: { selectedMedias, selectedPdfs }
			}
		}
	} = useGetShowroomSelectedMediaQuery();

	const { onSubmit, renderInput: renderUserConsentInput, acceptConsent } = useUserConsent();

	const { t } = useTranslation();
	const [cookies] = useCookies();
	const { enqueueNotification } = useCustomNotifications();
	const identify = useIdentifyUser();

	const [emailToSendError, setEmailToSendError] = React.useState(false);
	const [emailToSend, setEmailToSend] = React.useState<string>(
		cookies[`showroom-${tenant}`] && cookies[`showroom-${tenant}`].userViewerEmail ? cookies[`showroom-${tenant}`].userViewerEmail : ''
	);
	const [downloadOptionSelected, setDownloadOptionSelected] = React.useState('direct');

	const [getArchiveStatus, { data: getArchiveStatusData, stopPolling }] = useGetArchiveStatusLazyQuery({ pollInterval: 5000 });

	const closeDialog = React.useCallback(() => {
		setDialogIsOpen(false);
		setSelectedPdfs([]);
		setShowroomSelectedMedia([]);
		setLoading(false);
		if (stopPolling) stopPolling();
	}, [stopPolling]);

	const { downloadFiles } = useDownloadFiles();

	const buildTrackEvent = React.useCallback(
		(media: SelectedShowroomMedia | SelectedShowroomPdf) => {
			const isPdf = !!(media as SelectedShowroomPdf).publicId;
			return {
				eventType: 'download',
				mediaType: isPdf ? 'pdf' : isImage(media as SelectedShowroomMedia) ? 'image' : 'video',
				mediaId: isPdf ? (media as SelectedShowroomPdf).publicId : (media as SelectedShowroomMedia).cloudinaryId,
				showroomId: showroomObj.id,
				downloadQuality: isPdf ? QualityOptions.Original : mediaQuality
			};
		},
		[showroomObj?.id, mediaQuality]
	);

	const onSuccessHandler = React.useCallback(() => {
		const trackEvents = map([...selectedMedias, ...selectedPdfs], buildTrackEvent);
		if (trackEvents.length > 0) track(trackEvents, emailToSend);
		closeDialog();
	}, [closeDialog, emailToSend, selectedPdfs, track, buildTrackEvent, selectedMedias]);

	const onErrorHandler = React.useCallback(() => {
		setLoading(false);
		enqueueNotification({
			title: t('showroom.download.notification.error.title'),
			text: <span>{t('showroom.download.notification.error.content')}</span>
		});
	}, [enqueueNotification, t]);

	const directDownloadAction = React.useCallback(
		(generatedUrls: DownloadFile[]) => {
			downloadFiles(generatedUrls as unknown as DownloadFileHookInput[], {
				onError: onErrorHandler,
				waitForCompletion: false
			});
		},
		[downloadFiles, onErrorHandler]
	);

	const [generateShowroomArchiveZip] = useGenerateShowroomArchiveMutation();

	const [getZipFileQuery] = useGetZipFileLazyQuery({
		onCompleted: data => {
			directDownloadAction(data.archiveFiles);
			onSuccessHandler();
		},
		onError: onErrorHandler
	});

	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		if (!emailToSend && cookies[`showroom-${tenant}`] && cookies[`showroom-${tenant}`].userViewerEmail) {
			setEmailToSend(cookies[`showroom-${tenant}`].userViewerEmail);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cookies, tenant]);

	React.useEffect(() => {
		setDownloadOptionSelected(selectedMedias.length + selectedPdfs.length > 10 ? 'email' : 'direct');
	}, [selectedMedias, selectedPdfs]);

	React.useEffect(() => {
		if (!getArchiveStatusData) return;
		const {
			getArchive: { id, status }
		} = getArchiveStatusData;
		if (status === ArchiveStatus.Pending) return;
		stopPolling();
		if (status === ArchiveStatus.Completed) {
			getZipFileQuery({
				variables: {
					id
				}
			});
		} else {
			onErrorHandler();
		}
	}, [getArchiveStatusData, stopPolling, getZipFileQuery, onErrorHandler]);

	const exportMedia = React.useCallback(async () => {
		identify(
			{ id: emailToSend, email: emailToSend, externalGuest: true },
			{
				SalesforceId: showroomObj.tenant?.salesforceAccountId,
				sfparentid: showroomObj.tenant?.salesforceAccountParentId,
				EcoAccountName: showroomObj.tenant?.EcosystemAccountName,
				ecoAccountId: showroomObj.tenant?.EcosystemAccountID,
				Site: showroomObj.tenant?.name,
				siteId: showroomObj.tenant?.id
			}
		);
		const pdfIds = map(selectedPdfs, pdf => pdf.publicId);
		const variables: GenerateShowroomArchiveMutationVariables = {
			params: {
				tenant,
				showroomId: showroomObj.id,
				pdfIds,
				options: {
					recipients: downloadOptionSelected === 'email' ? [emailToSend] : [],
					quality: mediaQuality
				},
				mediaIds: selectedMedias.map(media => media.id)
			}
		};

		try {
			if (downloadOptionSelected === 'email') {
				await generateShowroomArchiveZip({
					variables
				});
				enqueueNotification({
					title: t('showroom.download.notification.email.success.title'),
					text: <span>{t('showroom.download.notification.email.success.text')}</span>,
					icon: <img src="/icons/emailSent.svg" alt=""></img>
				});
				onSuccessHandler();
			} else {
				const {
					data: { generateShowroomArchive }
				} = await generateShowroomArchiveZip({
					variables: {
						...variables,
						params: {
							...variables.params,
							isDirectDownload: true,
							options: {
								...variables.params.options,
								removeAfter: '5min'
							}
						}
					} as GenerateShowroomArchiveMutationVariables
				});
				enqueueNotification({
					title: t('showroom.download.notification.direct.success.title'),
					text: <span>{t('showroom.download.notification.direct.success.text')}</span>,
					icon: <img src="/icons/directDownload.svg" alt=""></img>
				});
				getArchiveStatus({ variables: { id: generateShowroomArchive.id } });
			}
		} catch (error) {
			onErrorHandler();
		}
	}, [
		identify,
		emailToSend,
		showroomObj,
		selectedPdfs,
		tenant,
		downloadOptionSelected,
		mediaQuality,
		selectedMedias,
		generateShowroomArchiveZip,
		enqueueNotification,
		t,
		onSuccessHandler,
		getArchiveStatus,
		onErrorHandler
	]);

	const dialogContent = React.useMemo(
		() => (
			<div className={classes.wrapper}>
				<RadioGroup
					id="downloadOptions"
					className={classes.radioButtonBox}
					name="downloadOptions"
					value={downloadOptionSelected}
					onChange={ev => setDownloadOptionSelected(ev.target.value)}
				>
					<FormControlLabel
						id="directDownloadOption"
						value="direct"
						control={<Radio />}
						label={t('showroom.download.dialog.direct.label')}
					/>
					<FormControlLabel id="emailDownloadOption" value="email" control={<Radio />} label={t('showroom.download.dialog.email.label')} />
				</RadioGroup>
				{downloadOptionSelected === 'email' ? (
					<>
						<TextField
							id="showroomDownloadDialogMailInput"
							variant="outlined"
							placeholder={t('showroom.download.dialog.input.placeholder')}
							fullWidth
							defaultValue={emailToSend}
							error={emailToSendError}
							onChange={ev => {
								setEmailToSend(ev.target.value.split(',')[0]);
								if (isEmail(emailToSend)) setEmailToSendError(false);
							}}
							onBlur={() => (!isEmail(emailToSend) ? setEmailToSendError(true) : setEmailToSendError(false))}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<MailIcon />
									</InputAdornment>
								)
							}}
						/>
						{renderUserConsentInput({
							wrapper: {
								className: classes.userConsentWrapper
							}
						})}
					</>
				) : selectedMedias.length > 10 ? (
					<div className={classes.directDownloadAlertMessageBox}>{t('showroom.download.dialog.direct.alert')}</div>
				) : null}
			</div>
		),
		[classes, downloadOptionSelected, emailToSend, emailToSendError, renderUserConsentInput, selectedMedias.length, t]
	);

	const onConfirmHandler = React.useCallback(async () => {
		const doExport = () => {
			setLoading(true);
			exportMedia();
		};
		if (downloadOptionSelected === 'email') {
			await onSubmit(async () => {
				doExport();
				await acceptConsent(emailToSend);
			});
		} else doExport();
	}, [downloadOptionSelected, onSubmit, exportMedia, acceptConsent, emailToSend]);

	return (
		<DialogConfirm
			open={isDialogOpen}
			title={<div className={classes.wrapper}>{t('showroom.download.dialog.title')}</div>}
			content={dialogContent}
			dismissText={t('showroom.download.dialog.dismiss')}
			confirmText={t('showroom.download.dialog.confirm')}
			confirmTypeButton="confirm"
			onDismiss={closeDialog}
			onConfirm={onConfirmHandler}
			confirmButtonDisabled={downloadOptionSelected === 'email' ? emailToSendError || !emailToSend : false}
			loading={loading}
		/>
	);
};

export default React.memo(ShowroomContentShare);
